import { numFont } from '../../utils/fonts';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  TextField,
  Typography,
  keyframes,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import ContactlessIcon from '@mui/icons-material/Contactless';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import CheckIcon from '@mui/icons-material/Check';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import BlockIcon from '@mui/icons-material/Block';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DecimalField from '../shared/DecimalField';
import { formatPriceWithCurrency } from '../../utils/vat';
import { partsSaleErrorMessage } from './salesUtils';
import { terminalErrorDetail } from '../../utils/terminalErrors';

const bounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

const roundCents = (value) => Math.round(value * 100) / 100;

/**
 * Fast-tender suggestions: the exact amount plus rounded-up bill-friendly sums.
 * Never suggests less than the amount due (a too-small quick button is a dead end).
 */
const tenderSuggestions = (due) => {
  const exact = roundCents(due);
  const candidates = [exact];
  [10, 20, 50, 100].forEach((step) => candidates.push(roundCents(Math.ceil(due / step) * step)));
  return [...new Set(candidates)].filter((v) => v >= exact).sort((a, b) => a - b).slice(0, 4);
};

function TerminalSelect({ terminals, terminalId, setTerminalId, label, sx }) {
  if (terminals.length <= 1) return null;
  return (
    <TextField
      select
      size="small"
      fullWidth
      label={label}
      value={terminalId}
      onChange={(e) => setTerminalId(e.target.value)}
      sx={sx}
      data-testid="terminal-select"
    >
      {terminals.map((tm) => (
        <MenuItem key={tm.id} value={tm.id}>{tm.name || tm.terminal_id}</MenuItem>
      ))}
    </TextField>
  );
}

function PayChoice({ icon, label, sub, onClick, disabled, testId }) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        py: 3,
        borderRadius: '14px',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.primary',
        textTransform: 'none',
        '&:hover': { borderColor: 'secondary.main', bgcolor: '#ebf4ff' },
      }}
    >
      <Box
        sx={{
          width: 52,
          height: 52,
          borderRadius: '14px',
          bgcolor: 'grey.100',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{label}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Button>
  );
}

/**
 * POS settlement flow: choose method → cash counting / card wait → receipt.
 * `settle(method)` is provided by the till (it POSTs counter_sale, or
 * settle_payment when a pending unpaid sale exists) and resolves to
 * { sale, invoice, settled, terminal_result }.
 *
 * A failed card capture lands on an explicit FAILURE stage: the sale and draft
 * invoice already exist, so the only safe moves are retry-card / cash / void —
 * never a fresh sale. The dialog can't be dismissed from that stage.
 *
 * Card without a Verstaani (Nexi) terminal is a MANUAL card payment: the seller
 * charges the customer on their own device (FlatPay etc.) and confirms here; the
 * sale is recorded as a card payment exactly like "Merkitse maksetuksi kortilla"
 * on a workshop invoice. That path is a single atomic request, so a failure
 * creates nothing and the cashier simply lands back on the method choice.
 */
function PaymentDialog({
  open,
  onClose,
  total,
  cartCount,
  terminals,
  terminalId,
  setTerminalId,
  settle,
  settleLeg,
  refundLeg,
  onVoid,
  pendingReceipt,
  onNewSale,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stage, setStage] = useState('method'); // method | cash | card | card_manual | failed | done
  const [tendered, setTendered] = useState('');
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Cash+card split legs [{method, amount}] — set when the cashier takes partial
  // cash and puts the rest on card; retained across a failed capture for retry.
  const [splitLegs, setSplitLegs] = useState(null);
  // Translated instruction for what to fix on the reader (app not in front, terminal
  // offline). Shown under the failure message so the seller isn't left guessing.
  const [terminalHint, setTerminalHint] = useState('');
  // What to put on the next card. Pre-filled with the whole remainder, but editable:
  // the usual reason a card is declined at the till is a limit, and the way out is to
  // charge this card less and the rest to another one.
  const [nextLegAmount, setNextLegAmount] = useState('');
  // The amount the reader is asking for right now, when it is one leg rather than the
  // whole balance. Null outside a per-leg capture.
  const [activeLegAmount, setActiveLegAmount] = useState(null);
  // Voiding a part-paid sale refunds the cards on the reader. If the customer has already
  // walked out there is no card to tap, so the refund runs as a back-office refund.
  const [customerAway, setCustomerAway] = useState(false);
  // "Terminal is busy, trying again (2/3)" under the waiting screen; empty otherwise.
  const [retryNotice, setRetryNotice] = useState('');

  useEffect(() => {
    if (open) {
      setTendered('');
      setError('');
      setTerminalHint('');
      setBusy(false);
      setSplitLegs(null);
      setNextLegAmount('');
      setActiveLegAmount(null);
      setCustomerAway(false);
      setRetryNotice('');
      if (pendingReceipt) {
        // Re-opened with an unpaid sale hanging — resume at the failure stage.
        setReceipt(pendingReceipt);
        setStage((pendingReceipt.recorded_legs || []).length > 0 ? 'split' : 'failed');
      } else {
        setReceipt(null);
        setStage('method');
      }
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // A Verstaani-integrated terminal captures the card itself; without one the
  // card is charged on the workshop's own device and only recorded here.
  const hasTerminal = terminals.length > 0;
  const tenderedNum = parseFloat(tendered) || 0;
  const dueTotal = receipt?.invoice?.total_incl_vat != null ? parseFloat(receipt.invoice.total_incl_vat) : total;
  const change = Math.max(0, tenderedNum - dueTotal);
  const shortfall =
    tendered !== '' && tenderedNum > 0 ? roundCents(Math.max(0, roundCents(dueTotal) - roundCents(tenderedNum))) : 0;
  const cardLegAmount = splitLegs ? parseFloat(splitLegs.find((l) => l.method === 'card')?.amount || 0) : null;
  // Server truth: what the sale has already taken, and what is still owed. A leg that
  // went through is money the acquirer holds, so the till must never recompute this
  // from its own idea of the total.
  const paidSoFar = (receipt?.invoice?.payments || []).reduce(
    (sum, entry) => sum + (parseFloat(entry.amount) || 0),
    0,
  );
  const remainingDue = roundCents(dueTotal - paidSoFar);
  const isPartiallyPaid = paidSoFar > 0;
  // What has gone through so far, for the green list. Per-leg responses carry recorded_legs
  // (with the masked PAN); a sale resumed from the old one-shot path only has payments.
  const recordedLegs = receipt?.recorded_legs
    ? receipt.recorded_legs
    : (receipt?.invoice?.payments || []).map((entry, i) => ({ id: i, payment_method: entry.method, amount: entry.amount }));
  const splitSummaryText = (legs) =>
    t('parts_sales.splitSummary', {
      cash: formatPriceWithCurrency(parseFloat(legs.find((l) => l.method === 'cash')?.amount || 0)),
      card: formatPriceWithCurrency(parseFloat(legs.find((l) => l.method === 'card')?.amount || 0)),
    });

  const runSettle = async (method, payments = null) => {
    setBusy(true);
    setError('');
    setTerminalHint('');
    setActiveLegAmount(null);
    try {
      const data = await settle(method, payments);
      setReceipt(data);
      if (data.settled) {
        setStage('done');
      } else {
        // Card capture failed — the sale + draft invoice exist, cart is spent.
        // The terminal's own `error` is raw vendor text (English/technical), so show
        // the translated message and keep only the short result_code as a reference
        // the seller can relay. The full detail stays in the API response for logs.
        // Not every unsettled response is a failure any more: a leg can go through and
        // still leave money owing. Only a terminal that actually refused is an error.
        const capturedSomething = (data.invoice?.payments || []).length > 0;
        const terminalRefused = data.terminal_result && data.terminal_result.success === false;
        if (capturedSomething && !terminalRefused) {
          setError('');
          setTerminalHint('');
          setStage('failed');
          return;
        }
        const terminalCode = data.terminal_result?.result_code;
        setError(
          terminalCode
            ? `${t('parts_sales.cardNotSettled')} (${terminalCode})`
            : t('parts_sales.cardNotSettled')
        );
        // Nexi keeps the actionable half in result_description — most often "the reader
        // app isn't in front", which every workshop hits on its first terminal sale.
        setTerminalHint(terminalErrorDetail(t, data.terminal_result));
        setStage('failed');
      }
    } catch (e) {
      setError(partsSaleErrorMessage(e, t, 'parts_sales.settleFailed'));
      // If nothing was created yet, method choice is safe; otherwise stay on failed.
      setStage(receipt && !receipt.settled ? 'failed' : 'method');
    } finally {
      setBusy(false);
    }
  };

  /** Take one tender of `amount` and land wherever the sale ends up. */
  const runLeg = async (method, amount) => {
    if (!settleLeg) return;
    setBusy(true);
    setError('');
    setTerminalHint('');
    setActiveLegAmount(roundCents(amount));
    setRetryNotice('');
    const onRetry = (attempt, total) => setRetryNotice(t('parts_sales.terminalBusyRetry', { attempt, total }));
    try {
      const data = await settleLeg({ method, amount: roundCents(amount).toFixed(2) }, remainingDue.toFixed(2), onRetry);
      setRetryNotice('');
      setReceipt(data);
      setNextLegAmount('');
      setStage(data.settled ? 'done' : 'split');
    } catch (e) {
      const body = e.response?.data;
      if (body?.invoice || body?.recorded_legs) {
        // The leg was refused but earlier ones stand; keep the seller on the same screen
        // with the real remaining balance rather than sending them back to the start.
        // `invoice.payments` is the {method, amount} shape the rest of this dialog reads.
        setReceipt((prev) => ({
          ...prev,
          ...body,
          invoice: body.invoice
            ? { ...prev?.invoice, ...body.invoice }
            : {
                ...prev?.invoice,
                payments: body.recorded_legs.map((leg) => ({ method: leg.payment_method, amount: leg.amount })),
              },
        }));
      }
      setRetryNotice('');
      setError(partsSaleErrorMessage(e, t, 'parts_sales.settleFailed'));
      setTerminalHint(terminalErrorDetail(t, body?.terminal_result));
      setStage('failed');
    } finally {
      setBusy(false);
    }
  };

  const splitLegsFromTender = () => [
    { method: 'cash', amount: tenderedNum.toFixed(2) },
    { method: 'card', amount: shortfall.toFixed(2) },
  ];

  const startCard = () => {
    setSplitLegs(null);
    if (!hasTerminal) {
      setError('');
      setTerminalHint('');
      setStage('card_manual');
      return;
    }
    setStage('card');
    runSettle('card');
  };

  const startSplit = () => {
    const legs = splitLegsFromTender();
    setSplitLegs(legs);
    if (!hasTerminal) {
      setError('');
      setTerminalHint('');
      setStage('card_manual');
      return;
    }
    setStage('card');
    runSettle('split', legs);
  };

  /** Split screen: enter the split from the method choice, with nothing attempted yet. */
  const startSplitStage = () => {
    setError('');
    setTerminalHint('');
    setSplitLegs(null);
    setNextLegAmount('');
    setStage('split');
  };

  /** Split screen: take the entered amount (default: everything still due) as ONE leg. */
  const takeLeg = (method) => {
    const amount = parseFloat(nextLegAmount) || remainingDue;
    if (amount <= 0 || amount > remainingDue + 0.001) {
      setError(t('parts_sales.errors.exceeds_remaining'));
      return;
    }
    // Only a reader capture has a waiting screen; a manual card leg is just recorded.
    if (method === 'card' && hasTerminal) setStage('card');
    runLeg(method, amount);
  };

  const retryCard = () => {
    if (stage === 'split') {
      takeLeg('card');
      return;
    }
    // The per-leg path: charge what the seller put in the box as its own leg. This is
    // how a two-card sale starts at the till (a declined full charge, then a smaller
    // amount on that card and the rest on another), and once part of the sale is paid
    // it is the only path the server accepts, so card 2 cannot disturb card 1's money.
    if (hasTerminal) {
      const amount = parseFloat(nextLegAmount) || remainingDue;
      const isPartialLeg = isPartiallyPaid || amount < remainingDue - 0.001;
      if (isPartialLeg) {
        if (amount > 0 && amount <= remainingDue + 0.001) {
          setStage('card');
          runLeg('card', amount);
        }
        return;
      }
    }
    if (!hasTerminal) {
      setError('');
      setTerminalHint('');
      setStage('card_manual');
      return;
    }
    if (splitLegs) {
      setStage('card');
      runSettle('split', splitLegs);
    } else {
      startCard();
    }
  };

  const confirmManualCard = () => {
    if (splitLegs) runSettle('split', splitLegs);
    else runSettle('card');
  };

  // Manual card confirm is reachable from three places; "back" returns to the one
  // it came from. Leaving the split path drops the legs so the cash stage recomputes
  // the shortfall from the tendered amount.
  const manualReturnStage = receipt && !receipt.settled ? 'failed' : splitLegs ? 'cash' : 'method';
  const leaveManualCard = () => {
    setError('');
    setTerminalHint('');
    if (manualReturnStage !== 'failed') setSplitLegs(null);
    setStage(manualReturnStage);
  };

  const voidSale = async () => {
    setBusy(true);
    setError('');
    setTerminalHint('');
    try {
      // A captured card leg is money the acquirer holds, so it goes back through the
      // reader first, one request per leg like the captures. Cash legs are handed back by
      // hand: the void accepts them and the parent tells the seller how much to return.
      const legs = receipt?.recorded_legs || [];
      const captured = legs.filter((leg) => leg.terminal_transaction);
      if (captured.length > 0 && refundLeg) {
        setStage('refunding');
        for (let i = 0; i < captured.length; i += 1) {
          setActiveLegAmount(parseFloat(captured[i].amount) || 0);
          setRetryNotice('');
          // eslint-disable-next-line no-await-in-loop
          const data = await refundLeg(captured[i].id, customerAway, (attempt, total) =>
            setRetryNotice(t('parts_sales.terminalBusyRetry', { attempt, total })));
          setRetryNotice('');
          setReceipt((prev) => ({ ...prev, ...data }));
        }
      }
      const cashBack = legs
        .filter((leg) => !leg.terminal_transaction)
        .reduce((sum, leg) => sum + (parseFloat(leg.amount) || 0), 0);
      await onVoid(cashBack);
      // Parent closes the dialog and clears its pending state.
    } catch (e) {
      const body = e.response?.data;
      if (body?.invoice) {
        setReceipt((prev) => ({ ...prev, ...body, invoice: { ...prev?.invoice, ...body.invoice } }));
      }
      setError(partsSaleErrorMessage(e, t, 'parts_sales.voidFailed'));
      setTerminalHint(terminalErrorDetail(t, body?.terminal_result || body));
      setStage('failed');
      setBusy(false);
    }
  };

  const printReceipt = () => {
    if (receipt?.invoice?.id) window.open(`/api/invoices/${receipt.invoice.id}/preview/`, '_blank');
  };

  // Never dismissible while an unpaid sale is hanging — it must be settled,
  // retried or voided; a backdrop/Esc close would strand it invisibly while
  // the cart stays editable.
  const pendingUnsettled = !!(receipt && !receipt.settled);
  // On the split screen with money already taken, the first thing the seller must see is
  // that it went through. The screen otherwise looks the same as before the tap.
  const splitSucceeded = stage === 'split' && isPartiallyPaid;
  const closable = stage !== 'card' && stage !== 'refunding' && stage !== 'failed' && !pendingUnsettled && !busy;

  return (
    <Dialog
      open={open}
      onClose={closable ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}
    >
      {/* METHOD CHOICE */}
      {stage === 'method' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{t('parts_sales.choosePaymentMethod')}</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2.5 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                {t('parts_sales.amountDue')}
              </Typography>
              <Typography sx={{ fontSize: 40, fontWeight: 700, ...numFont, letterSpacing: '-1px', lineHeight: 1.1 }}>
                {formatPriceWithCurrency(dueTotal)}
              </Typography>
            </Box>
            {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1.5 }}>
              <PayChoice
                icon={<CreditCardIcon sx={{ fontSize: 28, color: 'secondary.main' }} />}
                label={t('parts_sales.card')}
                sub={hasTerminal ? t('parts_sales.nexiTerminal') : t('parts_sales.cardManual')}
                onClick={startCard}
                testId="pay-card"
              />
              <PayChoice
                icon={<PaymentsIcon sx={{ fontSize: 28, color: 'secondary.main' }} />}
                label={t('parts_sales.cash')}
                sub={t('parts_sales.cashDrawer')}
                onClick={() => { setTendered(''); setStage('cash'); }}
                testId="pay-cash"
              />
            </Box>
            {/* Several tenders on one sale: two cards, or a card and cash, amounts chosen up
                front. Each tender is its own request, so the sale can stop half way. */}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CallSplitIcon />}
              onClick={startSplitStage}
              data-testid="pay-split"
              sx={{ mt: 1.5, py: 1.25, borderRadius: '14px', textTransform: 'none', justifyContent: 'flex-start', gap: 1 }}
            >
              <Box sx={{ textAlign: 'left' }}>
                <Typography sx={{ fontWeight: 700, fontSize: 15, lineHeight: 1.2 }}>{t('parts_sales.splitTitle')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('parts_sales.splitSub')}</Typography>
              </Box>
            </Button>
            {hasTerminal && (
              <TerminalSelect
                terminals={terminals}
                terminalId={terminalId}
                setTerminalId={setTerminalId}
                label={t('parts_sales.terminal')}
                sx={{ mt: 2 }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* CARD (MANUAL) — charged on the workshop's own terminal, recorded here */}
      {stage === 'card_manual' && (
        <Box data-testid="counter-sale-card-manual">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{t('parts_sales.cardManualTitle')}</Typography>
            <Button size="small" onClick={leaveManualCard} disabled={busy} sx={{ textTransform: 'none' }}>
              {t('parts_sales.back')}
            </Button>
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                {t('parts_sales.chargeOnCard')}
              </Typography>
              <Typography sx={{ fontSize: 40, fontWeight: 700, ...numFont, letterSpacing: '-1px', lineHeight: 1.1 }}>
                {formatPriceWithCurrency(splitLegs ? cardLegAmount : dueTotal)}
              </Typography>
              {splitLegs && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {splitSummaryText(splitLegs)}
                </Typography>
              )}
            </Box>
            <Alert severity="info" icon={<CreditCardIcon fontSize="inherit" />} sx={{ mb: 2, borderRadius: '10px' }}>
              {t('parts_sales.cardManualHint')}
            </Alert>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={<CheckIcon />}
              disabled={busy}
              onClick={confirmManualCard}
              data-testid="counter-sale-confirm-card"
              sx={{ borderRadius: '10px' }}
            >
              {t('parts_sales.confirmCardPayment')}
            </Button>
          </Box>
        </Box>
      )}

      {/* CARD — waiting on the terminal (a charge, or a refund before a void) */}
      {(stage === 'card' || stage === 'refunding') && (
        <Box sx={{ px: 4, py: 5, textAlign: 'center' }}>
          <Box
            sx={{
              width: 96, height: 96, borderRadius: '24px', mx: 'auto', mb: 2.5,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: '#ebf4ff',
            }}
          >
            <ContactlessIcon sx={{ fontSize: 52, color: '#3182ce' }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
            {stage === 'refunding' ? t('parts_sales.refundingCard') : t('parts_sales.waitingForCard')}
          </Typography>
          <Typography sx={{ fontSize: 32, fontWeight: 700, ...numFont, my: 1, letterSpacing: '-0.5px' }}>
            {formatPriceWithCurrency(activeLegAmount != null ? activeLegAmount : splitLegs ? cardLegAmount : dueTotal)}
          </Typography>
          {splitLegs && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {splitSummaryText(splitLegs)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            {t('parts_sales.tapOnTerminal')}
          </Typography>
          {retryNotice && (
            <Alert severity="warning" sx={{ mt: 1.5, textAlign: 'left' }} data-testid="counter-sale-terminal-retry">
              {retryNotice}
            </Alert>
          )}
          {/* Nexi only forwards the amount while the reader has Pääte or Raportit in
              front. Saying so here is cheaper than letting every workshop discover it
              through a failed first sale. */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('parts_sales.terminalAppHint')}
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 0.75 }}>
            {[0, 0.2, 0.4].map((d) => (
              <Box key={d} sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#3182ce', animation: `${bounce} 1s ${d}s infinite ease-in-out` }} />
            ))}
          </Box>
        </Box>
      )}

      {/* CASH */}
      {stage === 'cash' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{t('parts_sales.cashPayment')}</Typography>
            {receipt && !receipt.settled ? (
              <Button size="small" onClick={() => setStage('failed')} sx={{ textTransform: 'none' }}>
                {t('parts_sales.back')}
              </Button>
            ) : (
              <IconButton onClick={onClose} size="small" disabled={busy}><CloseIcon /></IconButton>
            )}
          </Box>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">{t('parts_sales.amountDue')}</Typography>
              <Typography sx={{ fontSize: 24, fontWeight: 700, ...numFont }}>
                {formatPriceWithCurrency(dueTotal)}
              </Typography>
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
              {t('parts_sales.tendered')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${tenderSuggestions(dueTotal).length}, minmax(0, 1fr))`, gap: 1, my: 1 }}>
              {tenderSuggestions(dueTotal).map((amt, i) => {
                const active = tenderedNum === amt && tendered !== '';
                return (
                  <Button
                    key={i}
                    onClick={() => setTendered(amt.toFixed(2))}
                    sx={{
                      py: 1.5,
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: active ? 'secondary.main' : 'divider',
                      bgcolor: active ? '#ebf4ff' : 'transparent',
                      color: active ? 'secondary.main' : 'text.primary',
                      ...numFont,
                      fontWeight: 600,
                    }}
                  >
                    {i === 0 ? t('parts_sales.exactAmount') : `${amt} €`}
                  </Button>
                );
              })}
            </Box>
            <DecimalField
              label={t('parts_sales.tenderedCustom')}
              value={tendered}
              onChange={setTendered}
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ my: 1 }}
            />
            {shortfall > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, bgcolor: '#fff8e6', borderRadius: '10px', my: 2 }}>
                <Typography sx={{ fontWeight: 600, color: '#8a6100' }}>{t('parts_sales.shortfall')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, ...numFont, color: '#8a6100' }}>
                  {formatPriceWithCurrency(shortfall)}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5, bgcolor: 'grey.50', borderRadius: '10px', my: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>{t('parts_sales.change')}</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 700, ...numFont, color: change > 0 ? 'text.primary' : 'text.disabled' }}>
                  {formatPriceWithCurrency(change)}
                </Typography>
              </Box>
            )}
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Button
              variant="contained"
              color="success"
              size="large"
              fullWidth
              startIcon={<CheckIcon />}
              disabled={busy || roundCents(tenderedNum) < roundCents(dueTotal)}
              onClick={() => runSettle('cash')}
              data-testid="counter-sale-confirm-cash"
              sx={{ borderRadius: '10px' }}
            >
              {t('parts_sales.confirmPayment')}
            </Button>
            {shortfall > 0 && (
              <>
                <TerminalSelect
                  terminals={terminals}
                  terminalId={terminalId}
                  setTerminalId={setTerminalId}
                  label={t('parts_sales.terminal')}
                  sx={{ mt: 2 }}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  fullWidth
                  startIcon={<CreditCardIcon />}
                  disabled={busy}
                  onClick={startSplit}
                  data-testid="counter-sale-split-card"
                  sx={{ borderRadius: '10px', mt: 1 }}
                >
                  {`${t('parts_sales.payRestWithCard')} · ${formatPriceWithCurrency(shortfall)}`}
                </Button>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* SPLIT / FAILED — one screen for "take the next tender". Entered from the method
          choice as Jaa maksu, or landed on after a declined capture. The sale and its draft
          invoice exist once the first leg has been attempted. */}
      {(stage === 'failed' || stage === 'split') && (
        <Box
          sx={{ px: 4, pt: 4.5, pb: 3.5, textAlign: 'center' }}
          data-testid={stage === 'split' ? 'counter-sale-split' : 'counter-sale-payment-failed'}
        >
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: stage === 'failed' ? '#fee2e2' : splitSucceeded ? '#e6ffed' : '#ebf4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            {stage === 'failed'
              ? <CreditCardOffIcon sx={{ fontSize: 40, color: '#e35454' }} />
              : splitSucceeded
                ? <CheckIcon sx={{ fontSize: 44, color: '#2f8c54' }} />
                : <CallSplitIcon sx={{ fontSize: 40, color: '#3182ce' }} />}
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }} data-testid="counter-sale-split-title">
            {stage === 'split'
              ? (splitSucceeded ? t('parts_sales.legSucceededTitle') : t('parts_sales.splitTitle'))
              : isPartiallyPaid && !error ? t('parts_sales.partialPaidTitle') : t('parts_sales.cardFailedTitle')}
          </Typography>
          {isPartiallyPaid && (
            <Box
              data-testid="counter-sale-partial-summary"
              sx={{ mt: 1.5, bgcolor: '#e6ffed', border: '1px solid #b7ebc6', borderRadius: '12px', px: 2, py: 1.25, textAlign: 'left' }}
            >
              {recordedLegs.map((leg) => (
                <Box key={leg.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                  <CheckIcon sx={{ fontSize: 20, color: '#2f8c54' }} />
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                    {leg.payment_method === 'cash' ? t('parts_sales.legTakenCash') : t('parts_sales.legChargedCard')}
                    {leg.card_number_masked ? ` · ${leg.card_number_masked}` : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ ...numFont, fontWeight: 700 }}>
                    {formatPriceWithCurrency(parseFloat(leg.amount) || 0)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.4 }}>
            {isPartiallyPaid ? t('parts_sales.remainingLabel') : t('parts_sales.amountDue')}
          </Typography>
          <Typography sx={{ fontSize: 28, fontWeight: 700, ...numFont, mb: 0.75, letterSpacing: '-0.5px' }}>
            {formatPriceWithCurrency(remainingDue)}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ my: 1.5, textAlign: 'left' }} data-testid="counter-sale-card-error">
              {error}
              {terminalHint && (
                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{terminalHint}</Typography>
              )}
            </Alert>
          )}
          {splitLegs && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {splitSummaryText(splitLegs)}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {stage === 'split' && !receipt
              ? t('parts_sales.splitInfo')
              : isPartiallyPaid
                ? t('parts_sales.saleCreatedPartial', { number: receipt?.invoice?.invoice_number || '' })
                : t('parts_sales.saleCreatedUnpaid', { number: receipt?.invoice?.invoice_number || '' })}
          </Typography>
          {stage === 'split' && !hasTerminal && (
            <Alert severity="info" icon={<CreditCardIcon fontSize="inherit" />} sx={{ mb: 2, borderRadius: '10px', textAlign: 'left' }}>
              {t('parts_sales.cardManualHint')}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {hasTerminal && (
              <TerminalSelect
                terminals={terminals}
                terminalId={terminalId}
                setTerminalId={setTerminalId}
                label={t('parts_sales.terminal')}
                sx={{ textAlign: 'left' }}
              />
            )}
            {(stage === 'split' || hasTerminal) && (
              <DecimalField
                value={nextLegAmount}
                onChange={setNextLegAmount}
                label={stage === 'split' ? t('parts_sales.splitLegAmount') : t('parts_sales.nextCardAmount')}
                placeholder={remainingDue.toFixed(2)}
                helperText={stage === 'split' ? undefined : t('parts_sales.nextCardAmountHint')}
                size="small"
                fullWidth
                disabled={busy}
                data-testid="counter-sale-next-leg-amount"
                sx={{ textAlign: 'left' }}
              />
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1 }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<CreditCardIcon />}
                disabled={busy}
                onClick={retryCard}
                data-testid="counter-sale-retry-card"
                sx={{ borderRadius: '10px' }}
              >
                {stage === 'split'
                  ? t('parts_sales.chargeCardLeg')
                  : isPartiallyPaid ? t('parts_sales.restAnotherCard') : t('parts_sales.retryCard')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PaymentsIcon />}
                disabled={busy}
                onClick={() => {
                  if (stage === 'split') {
                    takeLeg('cash');
                    return;
                  }
                  if (isPartiallyPaid) {
                    runLeg('cash', remainingDue);
                    return;
                  }
                  setError('');
                  setTendered('');
                  setSplitLegs(null);
                  setStage('cash');
                }}
                data-testid="counter-sale-fallback-cash"
                sx={{ borderRadius: '10px' }}
              >
                {stage === 'split' ? t('parts_sales.takeCashLeg') : t('parts_sales.cash')}
              </Button>
            </Box>
            {pendingUnsettled ? (
              <Button
                color="error"
                startIcon={<BlockIcon />}
                disabled={busy}
                onClick={voidSale}
                data-testid="counter-sale-void"
                sx={{ borderRadius: '10px', textTransform: 'none' }}
              >
                {t('parts_sales.voidSale')}
              </Button>
            ) : (
              <Button
                disabled={busy}
                onClick={() => { setError(''); setStage('method'); }}
                data-testid="counter-sale-split-back"
                sx={{ borderRadius: '10px', textTransform: 'none' }}
              >
                {t('parts_sales.back')}
              </Button>
            )}
            {isPartiallyPaid && (
              <Typography variant="caption" color="text.secondary" data-testid="counter-sale-void-hint">
                {t('parts_sales.voidRefundsCards')}
              </Typography>
            )}
            {isPartiallyPaid && (receipt?.recorded_legs || []).some((leg) => leg.terminal_transaction) && (
              <FormControlLabel
                sx={{ mx: 0, textAlign: 'left' }}
                control={
                  <Checkbox
                    size="small"
                    checked={customerAway}
                    onChange={(e) => setCustomerAway(e.target.checked)}
                    disabled={busy}
                    data-testid="counter-sale-customer-away"
                  />
                }
                label={<Typography variant="body2">{t('terminal.refundCustomerNotPresent')}</Typography>}
              />
            )}
            {pendingUnsettled && (
            <Typography variant="caption" color="text.secondary">
              {t('parts_sales.orHandleLater')}{' '}
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={() => receipt?.invoice?.id && navigate(`/invoices/${receipt.invoice.id}`)}
              >
                {t('parts_sales.openInvoice')}
              </Link>
            </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* DONE / RECEIPT */}
      {stage === 'done' && receipt && (
        <Box sx={{ px: 4, pt: 4.5, pb: 3.5, textAlign: 'center' }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#e6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
            <CheckIcon sx={{ fontSize: 44, color: '#2f8c54' }} />
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{t('parts_sales.saleComplete')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('parts_sales.itemsHandedOver', { count: cartCount })}
            {' · '}
            {receipt.invoice?.invoice_number}
            {' · '}
            {formatPriceWithCurrency(parseFloat(receipt.invoice?.total_incl_vat || 0))}
          </Typography>
          {(receipt.invoice?.payments || []).length > 0 && (
            <Box sx={{ mt: 1.5, mx: 'auto', maxWidth: 260, bgcolor: 'grey.50', borderRadius: '10px', px: 2, py: 1.25 }} data-testid="counter-sale-payment-breakdown">
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                {t('parts_sales.paymentBreakdown')}
              </Typography>
              {receipt.invoice.payments.map((leg, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">
                    {leg.method === 'cash' ? t('parts_sales.cash') : t('parts_sales.card')}
                  </Typography>
                  <Typography variant="body2" sx={{ ...numFont, fontWeight: 600 }}>
                    {formatPriceWithCurrency(parseFloat(leg.amount))}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 1.25 }}>
            <TaskAltIcon sx={{ fontSize: 15, color: '#2f8c54' }} />
            <Typography variant="caption" color="text.secondary">
              {t('parts_sales.paidInvoiceCreated')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2.75 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 1 }}>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={printReceipt} sx={{ borderRadius: '10px' }}>
                {t('parts_sales.printReceipt')}
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReceiptLongIcon />}
                onClick={() => receipt.invoice?.id && navigate(`/invoices/${receipt.invoice.id}`)}
                sx={{ borderRadius: '10px' }}
              >
                {t('parts_sales.openInvoice')}
              </Button>
            </Box>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              startIcon={<AddIcon />}
              onClick={onNewSale}
              data-testid="counter-sale-new"
              sx={{ borderRadius: '10px' }}
            >
              {t('parts_sales.newSale')}
            </Button>
          </Box>
        </Box>
      )}
    </Dialog>
  );
}

export default PaymentDialog;
